/**
 * Concrete dep-injection bindings for the entity-layer
 * `makeCreateWidgetAction(deps)` factory.
 *
 * Lives in the APP layer (`app/store/widget/`) because it value-imports from
 * `@/features/chats`, `@/app/store`, and `@/lib/config` — sources whose
 * direct value-imports would otherwise re-introduce the FSD layer-order
 * violation that the dependency inversion removed from
 * `entities/widget/model/widget-processor.utils.ts`. Only `app/` and `pages/`
 * are allowed to value-import from features.
 *
 * Consumed by `./widget-action.dispatcher`, which assembles all 13 per-widget
 * processor factories with this single dependency object — keeping the dep
 * surface easy to audit and uniform across widgets.
 *
 * Relocated from `features/widgets/processors/widget-processor.deps.ts`.
 */
import { useChatStore } from '@/features/chats';
import { useUIStore } from '@/app/store';
import type { MakeCreateWidgetActionDeps } from '@/entities/widget';

/**
 * Resolves the runtime dependency surface required by the
 * `makeCreateWidgetAction` entity factory. Each callback closes over the live
 * store / config singleton and forwards the call exactly as the previous
 * non-inverted utility did before the inversion.
 */
export const widgetProcessorDeps: MakeCreateWidgetActionDeps = {
  getIsChatConnected: () => useChatStore.getState().isConnected,
  openWidgetSidePanel: () => useUIStore.getState().openSidePanel('widget'),
  stagePendingWidget: (messageId, payload) => {
    // TODO: Refactor to avoid direct store import (legacy note from
    // before the dep inversion — see widget-processor.utils for context).
    useChatStore.setState((prev) => ({
      ...prev,
      _pendingWidgets: {
        ...prev._pendingWidgets,
        [messageId]: payload,
      },
    }));
  },
};
